import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ModalSheet } from "../dialogs/ModalSheet";
import {
  EMPTY_NAV_CUSTOMIZE,
  applyNavCustomize,
  moveNavKey,
  parseNavCustomize,
  type NavCustomize,
} from "./navCustomizeState";

function readStored(storageKey: string | null): NavCustomize {
  if (!storageKey || typeof window === "undefined") return EMPTY_NAV_CUSTOMIZE;
  try {
    return parseNavCustomize(window.localStorage.getItem(storageKey));
  } catch {
    return EMPTY_NAV_CUSTOMIZE;
  }
}

/**
 * The bottom nav's Customize preference, persisted under `storageKey`.
 *
 * `storageKey` is the caller's and should name the store, so two stores on one
 * device keep two arrangements (the seller portal's rule). Pass `null` while
 * the owner is not known yet — nothing is read or written until it is, so a
 * loading session can never overwrite a real preference with the default.
 *
 * The preference and the key it belongs to change together: on a key change
 * the state is re-read BEFORE commit (the render-phase reset React documents),
 * so the previous store's arrangement can never be written under the new key.
 * Storage that throws (private mode, quota) degrades to an in-memory
 * preference rather than an error.
 */
export function useNavCustomize<T extends { key: string }>(
  storageKey: string | null,
  items: readonly T[],
) {
  const [state, setState] = useState(() => ({ storageKey, value: readStored(storageKey) }));
  if (state.storageKey !== storageKey) {
    setState({ storageKey, value: readStored(storageKey) });
  }
  const customize = state.value;

  useEffect(() => {
    if (!state.storageKey || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(state.storageKey, JSON.stringify(state.value));
    } catch {
      /* storage full / unavailable — the preference lives for this visit only */
    }
  }, [state]);

  const { ordered, visible } = useMemo(
    () => applyNavCustomize(items, customize),
    [items, customize],
  );

  const update = (next: (value: NavCustomize) => NavCustomize) =>
    setState((current) =>
      current.storageKey === storageKey ? { ...current, value: next(current.value) } : current,
    );

  return {
    ordered,
    visible,
    hiddenKeys: customize.hidden,
    reorder: (order: string[]) => update((value) => ({ ...value, order })),
    hide: (key: string) =>
      update((value) => ({
        ...value,
        hidden: value.hidden.includes(key) ? value.hidden : [...value.hidden, key],
      })),
    show: (key: string) =>
      update((value) => ({ ...value, hidden: value.hidden.filter((k) => k !== key) })),
  };
}

export interface NavCustomizeItem {
  key: string;
  label: string;
  icon: ReactNode;
  href?: string;
  isActive?: boolean;
}

export interface NavCustomizeSheetLabels {
  title: string;
  close: string;
  show: string;
  hide: string;
  moveUp: string;
  moveDown: string;
}

/**
 * The Customize sheet: every destination in its current order, an eye to hide
 * or show it, and arrows to move it. The seller portal's sheet, row for row —
 * a row's label is still a link (through `renderLink`, the library has no
 * router), so a hidden destination stays one tap away.
 */
export function NavCustomizeSheet<T extends NavCustomizeItem>({
  isOpen,
  onClose,
  items,
  hiddenKeys,
  onReorder,
  onHide,
  onShow,
  labels,
  renderLink,
}: {
  isOpen: boolean;
  onClose: () => void;
  items: readonly T[];
  hiddenKeys: readonly string[];
  onReorder: (order: string[]) => void;
  onHide: (key: string) => void;
  onShow: (key: string) => void;
  labels: NavCustomizeSheetLabels;
  /** Renders a row's label as a link. Omit and the label is plain text. */
  renderLink?: (item: T, className: string, children: ReactNode, close: () => void) => ReactNode;
}) {
  const hidden = new Set(hiddenKeys);
  const keys = items.map((item) => item.key);

  const rowBase =
    "flex items-center gap-2.5 border-b border-[color:var(--hair)] px-3.5 py-2.5 last:border-b-0 bg-[color:var(--card)]";
  const ctrlBtn =
    "flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--soft)] hover:text-[color:var(--text-primary)] disabled:pointer-events-none disabled:opacity-30";
  const iconWrap =
    "shrink-0 text-[color:var(--text-secondary)] [&_svg]:h-[18px] [&_svg]:w-[18px]";

  return (
    <ModalSheet isOpen={isOpen} onClose={onClose} size="sm" surface="content" ariaLabel={labels.title}>
      <div data-ui="nav-customize" className="flex max-h-[80vh] flex-col">
        <div className="relative flex items-center justify-center px-4 py-4">
          <button
            type="button"
            onClick={onClose}
            aria-label={labels.close}
            className="absolute start-4 flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--soft)] text-[color:var(--text-secondary)] transition-colors hover:text-[color:var(--text-primary)]"
          >
            <XGlyph />
          </button>
          <h2 className="text-[15px] font-semibold text-[color:var(--text-primary)]">
            {labels.title}
          </h2>
        </div>

        <div className="overflow-y-auto px-4 pb-4">
          <ul className="overflow-hidden rounded-[16px] border border-[color:var(--card-br)]">
            {items.map((item, index) => {
              const isHidden = hidden.has(item.key);
              const body = (
                <>
                  <span className={`${iconWrap} ${isHidden ? "opacity-45" : ""}`}>{item.icon}</span>
                  <span
                    className={[
                      "min-w-0 flex-1 truncate text-[14px] font-medium",
                      isHidden
                        ? "text-[color:var(--text-muted)] line-through"
                        : "text-[color:var(--text-primary)]",
                    ].join(" ")}
                  >
                    {item.label}
                  </span>
                </>
              );
              const linkClass = "flex min-w-0 flex-1 items-center gap-2.5";
              return (
                <li key={item.key} className={rowBase} data-hidden={isHidden ? "true" : undefined}>
                  {item.href && renderLink ? (
                    renderLink(item, linkClass, body, onClose)
                  ) : (
                    <span className={linkClass}>{body}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => (isHidden ? onShow(item.key) : onHide(item.key))}
                    aria-label={`${isHidden ? labels.show : labels.hide}: ${item.label}`}
                    aria-pressed={isHidden}
                    className={ctrlBtn}
                  >
                    {isHidden ? <EyeGlyph /> : <EyeOffGlyph />}
                  </button>
                  <button
                    type="button"
                    onClick={() => onReorder(moveNavKey(keys, index, -1))}
                    disabled={index === 0}
                    aria-label={`${labels.moveUp}: ${item.label}`}
                    className={ctrlBtn}
                  >
                    <ChevronGlyph up />
                  </button>
                  <button
                    type="button"
                    onClick={() => onReorder(moveNavKey(keys, index, 1))}
                    disabled={index === items.length - 1}
                    aria-label={`${labels.moveDown}: ${item.label}`}
                    className={ctrlBtn}
                  >
                    <ChevronGlyph />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </ModalSheet>
  );
}

const GLYPH = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function XGlyph() {
  return (
    <svg {...GLYPH} width={18} height={18}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function ChevronGlyph({ up = false }: { up?: boolean }) {
  return (
    <svg {...GLYPH} width={18} height={18} strokeWidth={2.1}>
      <path d={up ? "m6 15 6-6 6 6" : "m6 9 6 6 6-6"} />
    </svg>
  );
}

function EyeGlyph() {
  return (
    <svg {...GLYPH} width={19} height={19}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffGlyph() {
  return (
    <svg {...GLYPH} width={19} height={19}>
      <path d="M10.7 5.1A9.9 9.9 0 0 1 12 5c6.5 0 10 7 10 7a13.2 13.2 0 0 1-2.2 3" />
      <path d="M6.6 6.6A13.2 13.2 0 0 0 2 12s3.5 7 10 7a9.9 9.9 0 0 0 5.4-1.6" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="m2 2 20 20" />
    </svg>
  );
}
